import type { ComponentInterface } from '@stencil/core';
import { Component, Host, h, Prop } from '@stencil/core';
import type { Color } from '../../interface';

@Component({
  tag: 'jackery-button',
  styleUrl: 'jackery-button.css',
  shadow: true,
})
export class JackeryButton implements ComponentInterface {

  /**
   * 是否禁用按钮交互
   */
  @Prop({ reflect: true }) disabled = false;

  /**
   * 加载状态
   */
  @Prop({ reflect: true }) loading = false;

  /**
   * 按钮颜色
   * 可选值: `"primary"`, `"success"`, `"danger"`, `"warning"`.
   */
  @Prop({ reflect: true }) color?: Color;

  /**
   * 按钮类型
   */
  @Prop() type: 'submit' | 'reset' | 'button' = 'button';

  /**
   * 按钮尺寸
   */
  @Prop({ reflect: true }) size?: 'sm' | 'md' | 'lg' | 'xl' = 'md';

  /**
   * 全宽显示
   * true: block, false: inline-block
   */
  @Prop({ reflect: false }) full = false;

  /**
   * 按钮形状，默认round
   */
  @Prop({ reflect: true }) shape: 'round' | 'square';

  /**
   * 超链接地址
   * 如果设置了href属性，将渲染成a标签
   */
  @Prop() href: string | undefined;

  /**
   * 按钮图标
   */
  @Prop() icon: string | undefined;

  /**
   * 按钮图标位置（默认在前）
   */
  @Prop() iconpos: 'start' | 'end' = 'start';

  private handleClick = (ev: Event) => {
    console.log(ev)
  };

  /**
   * 获取Loading图标颜色
   */
  getIconColor = () => {
    if(!this.color) return 'rgba(253, 80, 0, 1)';
    return null;
  }

  /**
   * 获取Loading图标大小
   */
  getIconSize = () => {
    if(!this.size) return '16px';
    switch (this.size) {
      case 'xl':
        return '22px'
      case 'lg':
        return '18px'
      case 'md':
        return '16px'
      case 'sm':
        return '14px'
      default:
        return '16px'
    }
  }

  /**
   * 渲染按钮图标
   */
  renderIcon = () => {
    if (this.icon && this.icon.startsWith("http")) {
      return <img class="jackery-button-icon" src={this.icon} alt="Loading"></img>;
    }
    if (this.loading) {
      return (
        <jackery-spinner size={this.getIconSize()} color={this.getIconColor()}></jackery-spinner>
      );
    }
    return null;
  };

  render() {
    const {href, type, disabled, size, color, full, shape, loading, iconpos} = this
    const finalSize = size === undefined ? 'small' : size;
    const TagType = href === undefined ? 'button' : ('a' as any);
    const shapeType = shape === undefined ? 'round' : shape;
    const iconPosType = iconpos === undefined ? 'start' : iconpos;
    const attrs = TagType === 'button' ? {type} : {
      href
    }

    return (
      <Host
        onClick={this.handleClick}
        class={{
          [`button-${finalSize}`]: finalSize !== undefined,
          [`button-color-${color}`]: color !== undefined,
          [`button-shape-${shapeType}`]: shapeType !== undefined,
          [`button-full`]: full,
        }}
      >
        <TagType
          {...attrs}
          part="native"
          class="button-native"
          disabled={disabled}
          loading={loading}
        >
            <span class="button-inner">
              <slot name="icon-only"></slot>
              <slot name="start"></slot>
              {iconPosType === 'start' && this.renderIcon()}
              <slot></slot>
              {iconPosType !== 'start' && this.renderIcon()}
              <slot name="end"></slot>
            </span>
        </TagType>
      </Host>
    );
  }
}
